import { useState } from 'preact/hooks';
import { useDateFormat, useTranslation } from '../i18n';
import type { ArticleListItem } from '../api/types';
import { articleHref, coverUrl, pickPublishDate, publicTags } from '../api/articles';

interface ArticleCardProps {
    article: ArticleListItem;
    /** Locale-prefixed root URL used to build the article deep-link. */
    rootHref: string;
}

/**
 * Cover-grid card for the Articles index. Mirrors the catalog
 * WorkCard pattern (cover image with scrim, italic display title
 * over the bottom of the cover, mono-uppercase metadata below) so the
 * platform's editorial surface stays visually consistent with the
 * catalog one.
 *
 * <para>Falls back to a textured gradient placeholder when no cover
 * is set or the configured cover URL 404s.</para>
 */
export function ArticleCard({ article, rootHref }: ArticleCardProps) {
    const t = useTranslation();
    const fmt = useDateFormat();
    const cover = coverUrl(article, 'article-card');
    const [failed, setFailed] = useState(false);
    const tags = publicTags(article);
    const publishedAt = pickPublishDate(article);
    const dateLabel = publishedAt ? fmt(publishedAt) : '';

    const placeholder = (
        <div class="grid h-full w-full place-items-center bg-gradient-to-br from-surface-2 to-surface-3">
            <span class="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-muted">
                {tags[0] ?? t('articles.kindArticle')}
            </span>
        </div>
    );

    return (
        <a
            href={articleHref(rootHref, article.id, article.slug)}
            class="group flex flex-col overflow-hidden rounded-md border border-rule bg-surface-raised transition hover:-translate-y-0.5 hover:border-accent-soft"
        >
            <div class="relative aspect-square overflow-hidden bg-surface-2">
                {cover && !failed ? (
                    <img
                        src={cover}
                        alt=""
                        loading="lazy"
                        class="h-full w-full object-cover transition group-hover:scale-[1.02]"
                        onError={() => setFailed(true)}
                    />
                ) : (
                    placeholder
                )}
                <div class="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 to-transparent" />
                <div class="absolute inset-x-0 bottom-0 p-3">
                    <h3 class="line-clamp-2 font-display text-[19px] italic font-normal text-fg-primary drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)] transition-colors group-hover:text-accent">
                        {article.title ?? t('articles.untitled')}
                    </h3>
                </div>
            </div>

            <div class="flex min-h-[80px] flex-1 flex-col gap-2 p-3">
                <div class="flex flex-wrap items-center gap-1.5">
                    {tags.slice(0, 3).map((tag) => (
                        <span
                            key={tag}
                            class="inline-flex items-center rounded-full border border-rule px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.14em] text-fg-muted"
                        >
                            {tag}
                        </span>
                    ))}
                </div>
                <div class="mt-auto flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.14em] text-fg-faint">
                    <span>{dateLabel}</span>
                    <span>
                        {t('articles.readingMinutes', { n: article.readingMinutes || 1 })}
                    </span>
                </div>
            </div>
        </a>
    );
}
